import i18next from '@/i18n';
import { RegionBaseData, RegionData, RegionExtraData } from '../data/region/Region';
import { ESeparateWallType, SeparateWallData } from '../data/separateWall/SeparateWall';
import { ERegionType } from '../data/type';
import { LineSeg2d } from '../math/LineSeg2d';
import { compareCoreAndOutline, PathFinder } from '../model/polygon/Region';
import { getUniqueId } from '../utils';
import { getWallSideStrArr } from '../utils/room';
import { getAreaSepIds, matchRegionsBySepWalls } from '../utils/region/match';
import RoomStore from './RoomStore';
import SeparateWallStore from './SeparateWallStore';
import { globalStores, TGlobalStores } from '.';
import { EFaceType } from '../entities/type';
import { EFaceSide } from '../types';
import { edgeToWallEdge } from '../utils/edge';
import { BaseRegionStore } from './base/baseRegionStore';
import { genStrsHash } from '../utils/hash';
import { IShapeSeg2d } from '../types/shape';
import { ArcSeg2d } from '../math/arcSeg2d';
import { getShapeSeg } from '../math/shape/other';
import { reverseShape } from '../utils/wallShape';
import { isArc } from '../utils/arc';

interface IEdgeInfo {
  wallId: string;
  side: EFaceSide;
  sepId: string;
  sepSide: EFaceSide;
  sepType: ESeparateWallType;
  startId: number;
  endId: number;
  regionId: string;
}
function createEdgeInfo(edgeInfo: Partial<IEdgeInfo>): IEdgeInfo {
  return {
    wallId: edgeInfo.wallId || '',
    side: edgeInfo.side || EFaceSide.Empty,
    sepId: edgeInfo.sepId || '',
    sepSide: edgeInfo.sepSide || EFaceSide.Single,
    sepType: edgeInfo.sepType || ESeparateWallType.Empty,
    startId: edgeInfo.startId || 0,
    endId: edgeInfo.endId || 0,
    regionId: edgeInfo.regionId || ''
  };
}

export class RegionStore extends BaseRegionStore {
  private _separateWallStore: SeparateWallStore;
  private _roomStore: RoomStore;

  constructor(gs: TGlobalStores) {
    super(gs);

    this._separateWallStore = gs.separateWallStore;
    this._roomStore = gs.roomStore;
  }

  getRegionDatas() {
    return this.getList();
  }

  getRegionData(id?: string) {
    if (!id) return;
    return this.get(id);
  }

  updateRegionBaseData(id: string, data: Partial<RegionBaseData>) {
    this.updateBaseData(id, data);
  }

  updateRegionExtraData(id: string, data: RegionExtraData) {
    this.updateExtraData(id, data);
  }

  addRegionData(regionData: RegionData) {
    this.add(regionData);
  }

  getRoomRegions(roomId?: string) {
    return this.getRegionDatas().filter((regionData) => regionData.relateRoomId === roomId);
  }

  refreshRegionDatasByRoomId(roomId: string) {
    const roomData = this._roomStore.getRoomData(roomId);
    if (!roomData) return;

    const currentRoomRegions = this.getRoomRegions(roomId);
    const swdInRoom = this._separateWallStore.getSepDatas({ roomIds: [roomId] });
    const removeRegionIdSet = new Set(currentRoomRegions.map((rd) => rd.id));

    // 壁芯计算
    const sepWallCoreAreas = this.getSepWallCoreAreas(roomId);

    // 获取房间内所有隔墙的轮廓线段
    const { segList, segInfos } = getSepWallSegs(swdInRoom);

    // 获取所有的区域轮廓
    const pathFinder = new PathFinder({
      segments: segList,
      segRelativeInfos: segInfos,
      isDirectedSegments: true,
      needClearFreeEdges: false,
      preserveOppositeEdge: true
    });
    const { areaResult, remainingEdges } = pathFinder.getArea();

    const { validOutlineArea, invalidOutlineArea, validCoreArea } = compareCoreAndOutline(
      sepWallCoreAreas,
      areaResult,
      true
    );

    // 新区域 → 旧分区：先按壁芯墙集合精确匹配，对不上的再看差集是不是全为零面积支墙来兜底认领。
    // 旧版本存下来的 relateSeparateWallIds 不含「搭在边界上、壁芯环要沿它出去再绕回来」的支墙
    // (本例：前室存档 31 堵 vs 现在算出来的 33 堵)，墙集 key 天然对不上，但描述的是同一块地；
    // 没有兜底它会被删掉重建成新 id 的分区 —— 名称/类型/材质全部回默认。
    const coreAreas = validCoreArea.map((coreArea) => ({
      corePathInfos: coreArea.edges.map((edge) => edgeToWallEdge(edge)),
      coreHoleInfos: coreArea.holesEdges.map((edges) => edges.map((edge) => edgeToWallEdge(edge)))
    }));
    const matchResults = matchRegionsBySepWalls(coreAreas, currentRoomRegions);

    // 更新/创建区域数据
    validOutlineArea.forEach((areaInfo, i) => {
      const regionPath = [areaInfo.path];
      const regionPathSwIds = [areaInfo.edges.map((edge) => edge.info.wallId)];

      if (areaInfo.holes.length) {
        regionPath.push(...areaInfo.holes);
        regionPathSwIds.push(areaInfo.holesEdges.flatMap((edges) => edges.map((edge) => edge.info.wallId)));
      }

      const { corePathInfos, coreHoleInfos } = coreAreas[i];
      const relateSwIds = getAreaSepIds(coreAreas[i]);
      const { regionId: matchedRegionId, exact } = matchResults[i];
      let regionId = matchedRegionId;
      const pathInfos = areaInfo.edges.map((edge) => edgeToWallEdge(edge));
      const holeInfos = areaInfo.holesEdges.map((edges) => edges.map((edge) => edgeToWallEdge(edge)));
      if (regionId) {
        removeRegionIdSet.delete(regionId);

        // 兜底认领的分区，墙集与存档本来就不是同一串；relateSeparateWallIds 只在 add 时写过一次、
        // 之后再没更新过，这里补上，否则下次刷新还要再兜一次，而且 refreshRegionDatas 里
        // 「隔墙已不在」的判断会一直停留在创建那一刻的旧墙表。
        if (!exact) {
          this.updateRegionBaseData(regionId, { relateSeparateWallIds: relateSwIds });
        }

        this.updateRegionExtraData(regionId, {
          pathInfos,
          holeInfos,
          corePathInfos,
          coreHoleInfos,
          height: 0
        });
      } else {
        regionId = getUniqueId();
        const rd: RegionData = {
          id: regionId,
          offGround: 0,
          relateSeparateWallIds: relateSwIds,
          pathInfos,
          holeInfos,
          corePathInfos,
          coreHoleInfos,
          height: 0,
          regionInfo: {
            type: ERegionType.Default,
            name: i18next.t('draw.house.parameters.unname')
          },
          relateRoomId: roomData.id,
          materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.FloorFace })
        };
        if (validOutlineArea.length === 1) {
          // 只有一个区域，继承房间属性
          rd.regionInfo = roomData.roomInfo;
          rd.materialInfo = roomData.materialInfo;
        }
        this.addRegionData(rd);
      }

      // 修改edge对象上的regionId
      areaInfo.edges.forEach((edge) => {
        edge.info = { ...edge.info, regionId };
      });
      areaInfo.holesEdges.forEach((holeEdge) => {
        holeEdge.forEach((edge) => {
          edge.info = { ...edge.info, regionId };
        });
      });
    });

    // 收集边界
    const edges = [];
    for (const areaInfo of validOutlineArea) {
      edges.push(...areaInfo.edges);

      areaInfo.holesEdges.forEach((holeEdge) => {
        edges.push(...holeEdge);
      });
    }
    for (const areaInfo of invalidOutlineArea) {
      edges.push(...areaInfo.edges);

      areaInfo.holesEdges.forEach((holeEdge) => {
        edges.push(...holeEdge);
      });
    }
    remainingEdges.forEach((edge) => {
      edge.info = {
        ...edge.info
      };
      edges.push(edge);
    });

    // 删除 旧分区
    removeRegionIdSet.forEach((id) => this.remove(id));

    // 更新pointStore
    // const removeRoomPointIds = globalStores.pointStore.getRoomPointIds(roomId);
    const newRoomPointSet = new Set<number>();
    edges.forEach((edge) => {
      // 端点必然是 pathFinder 点树里的节点；取不到就跳过，不给这条 edge 打上无效的 startId/endId
      const startPoint = pathFinder.getNode(edge.from);
      const endPoint = pathFinder.getNode(edge.to);
      if (!startPoint || !endPoint) return;

      const startPointHash = genStrsHash(getWallSideStrArr(startPoint.infos));
      const endPointHash = genStrsHash(getWallSideStrArr(endPoint.infos));

      newRoomPointSet.add(startPointHash);
      newRoomPointSet.add(endPointHash);

      edge.info = {
        ...edge.info,
        startId: startPointHash,
        endId: endPointHash
      };
    });

    globalStores.pointStore.refreshRoomSet(roomId, newRoomPointSet);

    // 将所有edge保存
    globalStores.edgeStore.updateSepEdges(
      roomId,
      edges.map((edge) => edgeToWallEdge(edge))
    );
  }

  refreshRegionDatas() {
    const roomDatas = this._roomStore.getRoomDatas();
    const preserveRoomIdSet = new Set<string>(roomDatas.map((roomData) => roomData.id));
    const regionDatas = this.getRegionDatas();

    // 删除房间已不在的分区 —— 下面的循环只遍历还存在的房间，这些分区没人管，必须在这里删。
    //
    // 「关联隔墙已不在」的不在这里删：基础隔墙的 id 是按房间轮廓的点哈希(startId/endId)认领的，
    // 边界墙的接头拓扑一变，同一段边界就会被删掉重建成新 id —— 分区的墙集里凭空多出死 id，
    // 而它围的还是同一块地。在这里删掉的话，refreshRegionDatasByRoomId 连按几何认领的机会都没有。
    // 认领不上的分区照样会被那边的 removeRegionIdSet 删掉，只是晚几行；房间那边(refreshRoomDatas)
    // 本来就没有这道前置删除，去留完全由匹配结果决定。
    regionDatas.forEach((regionData) => {
      if (!preserveRoomIdSet.has(regionData.relateRoomId)) {
        this.remove(regionData.id);
      }
    });

    roomDatas.forEach((roomData) => {
      this.refreshRegionDatasByRoomId(roomData.id);
    });
  }

  getSepWallCoreAreas(roomId: string) {
    const swdInRoom = this._separateWallStore.getSepDatas({ roomIds: [roomId] });

    // 获取房间内所有隔墙的轮廓线段
    const segList: IShapeSeg2d[] = [];
    const segInfos: IEdgeInfo[] = [];
    for (const swdData of swdInRoom) {
      switch (swdData.type) {
        case ESeparateWallType.BaseVirtualWall:
          {
            // 基础虚拟墙
            const seg = isArc(swdData)
              ? new ArcSeg2d({
                  start: swdData.start,
                  end: swdData.end,
                  center: swdData.center,
                  clockwise: swdData.clockwise
                })
              : new LineSeg2d(swdData.start, swdData.end);
            segList.push(seg);
            segInfos.push(
              createEdgeInfo({ sepId: swdData.id, wallId: swdData.relativeWallId, side: swdData.relativeWallSide })
            );
          }
          break;
        default:
          {
            // 其他隔墙添加双向中心线
            const seg = isArc(swdData)
              ? new ArcSeg2d({
                  start: swdData.start,
                  end: swdData.end,
                  center: swdData.center,
                  clockwise: swdData.clockwise
                })
              : new LineSeg2d(swdData.start, swdData.end);
            const oppositeSeg =
              seg instanceof ArcSeg2d ? seg.getOppositeArc() : new LineSeg2d(swdData.end, swdData.start);
            segList.push(seg, oppositeSeg);
            segInfos.push(createEdgeInfo({ sepId: swdData.id }), createEdgeInfo({ sepId: swdData.id }));
          }
          break;
      }
    }

    const pathFinder = new PathFinder({
      segments: segList,
      segRelativeInfos: segInfos,
      isDirectedSegments: true,
      needClearFreeEdges: true,
      preserveOppositeEdge: true
    });
    const { areaResult } = pathFinder.getArea();
    return areaResult;
  }

  clearAll() {
    this.clear();
  }

  /** 入参可能是完整的 RegionData（新存档），也可能只有基础字段（老存档），故派生字段按可选处理 */
  coverDatas(regionDataBases: (RegionBaseData & Partial<RegionExtraData>)[]) {
    this.clearAll();

    for (const rb of regionDataBases) {
      // corePathInfos 是第三轮按几何认出「同一块地、只是隔墙 id 换了」的 key，清空会让名称与材质全丢。
      // 更老的存档没有它，兜底成空数组，第三轮自动跳过。其余派生几何交给 refreshData 重算，与 RoomStore 一致。
      const { corePathInfos = [] } = rb;
      const regionData = {
        ...rb,
        height: 0,
        pathInfos: [],
        holeInfos: [],
        corePathInfos,
        coreHoleInfos: []
      };
      this.addRegionData(regionData);
    }
  }
}

function getSepWallSegs(swdInRoom: SeparateWallData[]) {
  const segList: IShapeSeg2d[] = [];
  const segInfos: {
    wallId: string;
    side: EFaceSide;
    sepId: string;
    sepSide: EFaceSide;
    sepType: ESeparateWallType;
    startId: number;
    endId: number;
    regionId: string;
  }[] = [];

  for (const swdData of swdInRoom) {
    if (swdData.type === ESeparateWallType.BaseVirtualWall) {
      // 基础虚拟墙
      const seg = isArc(swdData)
        ? new ArcSeg2d({
            start: swdData.start,
            end: swdData.end,
            center: swdData.center,
            clockwise: swdData.clockwise
          })
        : new LineSeg2d(swdData.start, swdData.end);
      segList.push(seg);
      segInfos.push(
        createEdgeInfo({
          wallId: swdData.relativeWallId,
          side: swdData.side,
          sepId: swdData.id,
          sepSide: EFaceSide.Single,
          sepType: ESeparateWallType.BaseVirtualWall
        })
      );
      continue;
    }

    if (swdData.type === ESeparateWallType.VirtualWall) {
      // 虚拟墙
      const seg = isArc(swdData)
        ? new ArcSeg2d({
            start: swdData.start,
            end: swdData.end,
            center: swdData.center,
            clockwise: swdData.clockwise
          })
        : new LineSeg2d(swdData.start, swdData.end);
      const oppositeSeg = seg instanceof ArcSeg2d ? seg.getOppositeArc() : new LineSeg2d(swdData.end, swdData.start);
      segList.push(seg);
      segInfos.push(
        createEdgeInfo({
          sepId: swdData.id,
          sepSide: EFaceSide.MainLeftSide,
          sepType: ESeparateWallType.VirtualWall
        })
      );
      segList.push(oppositeSeg);
      segInfos.push(
        createEdgeInfo({
          sepId: swdData.id,
          sepSide: EFaceSide.MainRightSide,
          sepType: ESeparateWallType.VirtualWall
        })
      );
      continue;
    }

    const startShape = swdData.drawSeparateWallInfo.startShape;
    const endShape = swdData.drawSeparateWallInfo.endShape;

    // 左边线
    segList.push(
      isArc(swdData)
        ? new ArcSeg2d({
            start: startShape[0],
            end: endShape[0],
            center: swdData.center,
            clockwise: swdData.clockwise
          })
        : new LineSeg2d(startShape[0], endShape[0])
    );
    segInfos.push(
      createEdgeInfo({
        sepId: swdData.id,
        sepSide: EFaceSide.MainLeftSide,
        sepType: swdData.type
      })
    );

    // 右边线
    segList.push(
      swdData.center
        ? new ArcSeg2d({
            start: endShape[endShape.length - 1],
            end: startShape[startShape.length - 1],
            center: swdData.center,
            clockwise: !swdData.clockwise
          })
        : new LineSeg2d(endShape[endShape.length - 1], startShape[startShape.length - 1])
    );
    segInfos.push(
      createEdgeInfo({
        sepId: swdData.id,
        sepSide: EFaceSide.MainRightSide,
        sepType: swdData.type
      })
    );

    // 起点边线
    const reverseStartShape = reverseShape(startShape);
    for (let i = 0; i < reverseStartShape.length - 1; i++) {
      segList.push(
        getShapeSeg({
          start: reverseStartShape[i],
          end: reverseStartShape[i + 1],
          ...reverseStartShape[i]
        })
      );
      segInfos.push(
        createEdgeInfo({
          sepId: swdData.id,
          sepSide: EFaceSide.StartSide,
          sepType: swdData.type
        })
      );
    }

    // 终点边线
    for (let i = 0; i < endShape.length - 1; i++) {
      segList.push(
        getShapeSeg({
          start: endShape[i],
          end: endShape[i + 1],
          ...endShape[i]
        })
      );
      segInfos.push(
        createEdgeInfo({
          sepId: swdData.id,
          sepSide: EFaceSide.EndSide,
          sepType: swdData.type
        })
      );
    }
  }

  return { segList, segInfos };
}
